import { useState } from "react";
import { savePin, verifyPin } from "./usePinLock";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

interface PinScreenProps {
  mode: "setup" | "verify";
  title: string;
  subtitle: string;
  onSuccess: () => void;
}

export function PinScreen({ mode, title, subtitle, onSuccess }: PinScreenProps) {
  const [pin, setPin] = useState("");
  // En modo setup pedimos el PIN dos veces para evitar errores de dedo al configurarlo.
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [closing, setClosing] = useState(false);

  const effectiveSubtitle =
    mode === "setup" && firstPin ? "Repite el PIN para confirmar" : subtitle;

  async function handleComplete(value: string) {
    if (mode === "verify") {
      const ok = await verifyPin(value);
      if (ok) {
        setClosing(true);
        setTimeout(onSuccess, 600);
      } else {
        setError(true);
        setTimeout(() => {
          setError(false);
          setPin("");
        }, 400);
      }
      return;
    }

    // mode === "setup"
    if (!firstPin) {
      setFirstPin(value);
      setPin("");
      return;
    }
    if (value === firstPin) {
      await savePin(value);
      setClosing(true);
      setTimeout(onSuccess, 600);
    } else {
      setError(true);
      setTimeout(() => {
        setError(false);
        setFirstPin(null);
        setPin("");
      }, 400);
    }
  }

  function press(key: string) {
    if (key === "" || closing) return;
    if (key === "del") {
      setPin((p) => p.slice(0, -1));
      return;
    }
    if (pin.length >= 4) return;
    const next = pin + key;
    setPin(next);
    if (next.length === 4) {
      setTimeout(() => handleComplete(next), 250);
    }
  }

  return (
    <div
      className={`fixed inset-0 z-[60] mx-auto flex max-w-[430px] flex-col items-center justify-center gap-1.5 px-6 py-8 transition-all duration-500 ${
        closing ? "pointer-events-none scale-105 opacity-0" : "opacity-100"
      }`}
      style={{
        background:
          "radial-gradient(600px 400px at 50% 18%, rgba(46,107,255,.16), transparent 65%), #05070D",
      }}
    >
      <div
        className="mb-3.5 grid h-16 w-16 place-items-center rounded-card font-display text-2xl font-extrabold tracking-[0.02em] text-white"
        style={{
          background: "linear-gradient(160deg,#3F7BFF,#2E6BFF)",
          boxShadow: "0 10px 40px rgba(46,107,255,.45), inset 0 1px 0 rgba(255,255,255,.25)",
        }}
      >
        VF
      </div>
      <div className="font-display text-2xl font-bold uppercase tracking-[0.04em]">{title}</div>
      <div className="mb-6 font-mono text-[10.5px] uppercase tracking-[0.14em] text-muted">
        {effectiveSubtitle}
      </div>

      <div className={`mb-8 flex gap-4 ${error ? "animate-pulse" : ""}`}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-[13px] w-[13px] rounded-full border-[1.5px] transition-all duration-150 ${
              i < pin.length
                ? "scale-110 border-accent bg-accent shadow-[0_0_12px_rgba(46,107,255,.8)]"
                : "border-[rgba(110,160,255,.4)]"
            } ${error ? "border-red-500 bg-red-500" : ""}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-x-6 gap-y-3.5">
        {KEYS.map((key, i) => (
          <button
            key={i}
            aria-label={key === "del" ? "Borrar" : key || undefined}
            onClick={() => press(key)}
            className={`grid h-[68px] w-[68px] place-items-center rounded-full font-mono text-[22px] font-medium text-text transition-transform active:scale-[.92] active:bg-accent-soft ${
              key === "" ? "pointer-events-none bg-transparent" : "border border-line bg-white/[.045]"
            } ${key === "del" ? "border-none bg-transparent" : ""}`}
          >
            {key === "del" ? (
              <svg viewBox="0 0 24 24" className="h-[22px] w-[22px] stroke-muted" fill="none" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 6H8l-5 6 5 6h13a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z" />
                <path d="M12 9l6 6M18 9l-6 6" />
              </svg>
            ) : (
              key
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
